import { cn } from '@/lib/utils';
import { circleShape } from '@/lib/shapeStyles';
import { useState, useEffect } from 'react';

/**
 * LoadingSpinner - Unified loading component for the entire app
 * 
 * USAGE GUIDELINES:
 * 
 * 1. **variant="full-page"** - For lazy-loaded routes (entire page loading)
 *    - Shows gradient background + frosted glass panel
 *    - Example: <LoadingSpinner variant="full-page" title="Loading Page" />
 * 
 * 2. **variant="panel"** - For in-page data loading (default for most pages)
 *    - Shows frosted glass panel in content area
 *    - Automatically has 250ms delay to prevent flash
 *    - Example: <LoadingSpinner variant="panel" title="Loading Cards" />
 * 
 * 3. **variant="inline"** - For page sections or card loading states
 *    - Just the spinner, no panel
 *    - Has 250ms delay by default
 *    - Example: <LoadingSpinner variant="inline" size="md" />
 * 
 * 4. **For buttons** - DO NOT use LoadingSpinner!
 *    - Use Loader2 directly: <Loader2 className="h-4 w-4 animate-spin" />
 *    - Keeps buttons compact without extra padding or text
 * 
 * 5. **Skeletons** - DO NOT USE. Per user mandate, no shimmering/pulsing
 *    placeholders anywhere in the app. Use this spinner for initial loads
 *    and a small inline spinner for "load more" pagination.
 *
 * ANTI-PATTERNS (DO NOT USE):
 * ❌ Skeleton/shimmer placeholders of any kind
 * ❌ Using LoadingSpinner in buttons (use Loader2 directly)
 * ❌ Creating custom loading states (use this component)
 */

interface LoadingSpinnerProps {
	variant?: 'full-page' | 'panel' | 'inline';
	title?: string;
	subtitle?: string;
	size?: 'sm' | 'md' | 'lg';
	delayMs?: number;
}

// Module-level continuity tracker. When one LoadingSpinner unmounts and
// another mounts within BRIDGE_MS (e.g. Suspense fallback → in-page data
// spinner handoff), the second one skips its show-delay so the user sees a
// single uninterrupted wheel instead of spinner → gap → spinner.
const BRIDGE_MS = 400;
let lastSpinnerVisibleAt = 0;

export const LoadingSpinner = ({
	variant = 'panel',
	title,
	subtitle,
	size = 'md',
	delayMs
}: LoadingSpinnerProps) => {
	// Smart defaults: 250ms delay for all variants to prevent flash
	const defaultDelay = 250;
	const withinBridge = Date.now() - lastSpinnerVisibleAt < BRIDGE_MS;
	const effectiveDelay = delayMs !== undefined
		? delayMs
		: (withinBridge ? 0 : defaultDelay);
	// Only show subtitle text for panel/full-page variants by default
	const effectiveSubtitle = subtitle !== undefined 
		? subtitle 
		: (variant === 'inline' ? undefined : 'This may take a moment...');
	const [showSpinner, setShowSpinner] = useState(effectiveDelay === 0);
	// When we skip the delay because a previous spinner just unmounted, also
	// skip fade-in so the handoff reads as one continuous wheel instead of a
	// second spinner blinking in on top of the first.
	const skipFadeIn = withinBridge && effectiveDelay === 0;

	useEffect(() => {
		if (effectiveDelay > 0) {
			const timer = setTimeout(() => setShowSpinner(true), effectiveDelay);
			return () => clearTimeout(timer);
		}
	}, [effectiveDelay]);

	// Track visibility for bridging: refresh timestamp while shown, and on unmount.
	useEffect(() => {
		if (!showSpinner) return;
		lastSpinnerVisibleAt = Date.now();
		const interval = setInterval(() => {
			lastSpinnerVisibleAt = Date.now();
		}, 100);
		return () => {
			clearInterval(interval);
			lastSpinnerVisibleAt = Date.now();
		};
	}, [showSpinner]);


	const spinnerSizes = {
		sm: 'w-8 h-8 border-2',
		md: 'w-16 h-16 border-4',
		lg: 'w-20 h-20 border-4'
	};

	const spinnerSize = spinnerSizes[size];

	if (!showSpinner) return null;

	const spinnerElement = (
		<div
			className={cn(
				spinnerSize,
				'border-active-gold/25 border-t-active-gold animate-spin',
				circleShape
			)}
		/>
	);

	const textElement = (title || effectiveSubtitle) && (
		<div className="flex flex-col items-center gap-2">
			{title && <p className="text-lg font-medium text-foreground">{title}</p>}
			{effectiveSubtitle && <p className="text-sm text-muted-foreground/70">{effectiveSubtitle}</p>}
		</div>
	);

	// Inline variant - just spinner + optional text
	if (variant === 'inline') {
		return (
			<div className="flex items-center justify-center gap-4 py-8" role="status" aria-live="polite">
				{spinnerElement}
				{textElement}
			</div>
		);
	}

	// Panel variant - frosted glass panel
	if (variant === 'panel') {
		return (
			<div className="flex flex-col items-center justify-center min-h-[60dvh] flex-1">
				<div
					className={cn(
						'glass-obsidian rounded-lg p-8 flex flex-col items-center gap-6',
						!skipFadeIn && 'animate-fade-in',
					)}
					role="status"
					aria-live="polite"
				>
					{spinnerElement}
					{textElement}
				</div>
			</div>
		);
	}

	// Full-page variant - gradient background + panel
	return (
		<div
			className="min-h-screen-safe flex flex-col items-center justify-center gap-6"
			style={{ background: 'var(--gradient-content-bg)' }}
		>
			<div
				className={cn(
					'glass-obsidian rounded-lg p-8 flex flex-col items-center gap-6',
					!skipFadeIn && 'animate-fade-in',
				)}
				role="status"
				aria-live="polite"
			>
				{spinnerElement}
				{textElement}
			</div>
		</div>
	);
};
